import React, { useMemo, useState, useEffect } from 'react';
import {
	Box,
	Button,
	Divider,
	Dialog,
	Drawer,
	Typography,
	useMediaQuery,
	useTheme,
	CircularProgress,
} from '@mui/material';
import ErrorIcon from '../../assets/error.svg';
import ExitIcon from '../../assets/exit.svg';
import ConfirmIcon from '../../assets/check.svg';
import cancelIcon from '../../assets/cancel.svg';
import CallPickedIcon from '../../assets/CallPicked.svg';
import CallInProgressIcon from '../../assets/CallInProgress.svg';
import VoiceJourneyFooter from './VoiceJourneyFooter';
import {
	PAGE_HEADING_ID,
	VOICE_ERROR_DETAILS_TITLE_ID,
} from '../../utils/pageHeading.utils';
import { visuallyHiddenSx, keyboardFocusVisibleSx } from '../../utils/a11y.utils';

const VoiceJourneyRedirectionCard = ({
	statusCode,
	errorCode,
	errorMessage,
	errorDetails,
	customDetails,
}) => {
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));
	const [showDetails, setShowDetails] = useState(false);
	const [liveMessage, setLiveMessage] = useState('');
	const fiuName = global?.entityName;
	const mobileFooterHeight = 52;

	const technicalErrorCodes = useMemo(
		() =>
			new Set([
				'UNAUTHORIZED',
				'BAD_REQUEST',
				'USER_MISMATCHED',
				'FIU_MISMATCHED',
				'ACCOUNT_NOT_LINKED',
				'FI_TYPE_MISMATCHED',
				'CANNOT_SERVE_REQUEST',
				'INTERNAL_SERVER_ERROR',
			]),
		[],
	);

	const technicalErrorConfig = {
		UNAUTHORIZED: {
			message: `Please enable vAAni to work on ${fiuName}.`,
			details: [],
		},
		BAD_REQUEST: {
			message:
				'The following required value(s) are missing or invalid in the request.',
			details: ['ID', 'CONSENT_ID'],
		},
		USER_MISMATCHED: {
			message: `Please enable vAAni to work on ${fiuName}.`,
			details: [],
		},
		FIU_MISMATCHED: {
			message: 'Please check the FIU name in the consent request.',
			details: [],
		},
		ACCOUNT_NOT_LINKED: {
			message: 'Please link the selected account, before initiating the call.',
			details: [],
		},
		FI_TYPE_MISMATCHED: {
			message:
				'There appears to be a mismatch between the Financial Information (FI) type and the selected accounts.',
			details: [],
		},
		CANNOT_SERVE_REQUEST: {
			message: 'The AI agent is not configured. Please check and try again.',
			details: [],
		},
		INTERNAL_SERVER_ERROR: {
			message: 'Internal server error. Please try again.',
			details: [],
		},
	};

	// Status configuration map
	const statusConfig = {
		200: {
			icon: ConfirmIcon,
			iconSize: '100px',
			title: 'Consent Approved by Customer',
			subtitle: `${global.entityName} will now be able to access the bank statements shared`,
		},
		201: {
			icon: cancelIcon,
			iconSize: '100px',
			title: 'Consent Rejected by Customer',
			subtitle: `${global.entityName} cannot access their bank statements. You can try reinitiating a new call to the customer`,
		},
		411: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Failed to initiate the call',
		},
		412: {
			icon: ExitIcon,
			iconSize: '100px',
			title: 'Customer ended the call',
			subtitle:
				'The conversation ended early. You may try calling again if needed.',
		},
		413: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Automated call ended',
			subtitle:
				'Start another call if you need to reconnect with the customer.',
		},
		414: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Customer did not answer the call',
			subtitle: 'Try placing the call again or reach out at a different time.',
		},
		415: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Maximum call duration reached',
			subtitle:
				'If the conversation needs to continue, you can start another call.',
		},
		416: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call went to voicemail',
			subtitle: 'The customer may be unavailable. Try calling again later.',
		},
		417: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'No response from the customer',
			subtitle:
				'The customer did not respond during the call. You can try placing the call again.',
		},
		418: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
		},
		419: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Customer declined the call',
			subtitle: 'The customer may be busy. You can try again later.',
		},
		420: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Customer did not answer the call',
			subtitle: 'Try placing the call again or reach out at a different time.',
		},
		421: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be connected',
			subtitle:
				'We were unable to connect the call to the customer. Please try again later.',
		},
		422: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call disconnected',
			subtitle:
				'The call was disconnected due to network issues. You can try placing the call again.',
		},
		423: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Something Went Wrong',
			subtitle: 'An error occurred. Please try again',
		},
		424: {
			icon: 'spinner',
			iconSize: '56px',
			title: 'Initiating Call...',
			subtitle: '',
		},
		425: {
			icon: 'spinner',
			iconSize: '56px',
			title: 'Calling Customer...',
			subtitle: '',
		},
		426: {
			icon: CallPickedIcon,
			iconSize: '56px',
			title: 'Customer picked the call',
			subtitle: '',
		},
		427: {
			icon: CallInProgressIcon,
			iconSize: '56px',
			title: 'Voice journey in progress...',
			subtitle: '',
		},
		428: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call Initiation Failed',
			subtitle:
				'Call already initiated with this URL. Please use a new URL to proceed.',
		},
		432: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
			errorCode: 'UNAUTHORIZED',
		},
		433: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
			errorCode: 'BAD_REQUEST',
		},
		434: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
			errorCode: 'USER_MISMATCHED',
		},
		435: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
			errorCode: 'FIU_MISMATCHED',
		},
		436: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
			errorCode: 'ACCOUNT_NOT_LINKED',
		},
		437: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
			errorCode: 'FI_TYPE_MISMATCHED',
		},
		438: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
			errorCode: 'CANNOT_SERVE_REQUEST',
		},
		439: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call already processed',
			subtitle:
				'Looks like this consent request has already been made. Please reinitiate the call to request for data.',
		},
		440: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Call could not be completed',
			subtitle: 'An issue occurred while placing the call. Please try again.',
			errorCode: 'INTERNAL_SERVER_ERROR',
		},
		default: {
			icon: ErrorIcon,
			iconSize: '56px',
			title: 'Something went wrong',
			subtitle: 'Please try again.',
		},
	};

	const config = statusConfig[statusCode] || statusConfig.default;
	const technicalError =
		config.errorCode && technicalErrorCodes.has(config.errorCode)
			? technicalErrorConfig[config.errorCode]
			: null;
	const resolvedErrorCode = errorCode || config.errorCode;
	const shouldUseResponseErrorData = resolvedErrorCode === 'BAD_REQUEST';
	const resolvedTechnicalMessage =
		shouldUseResponseErrorData &&
		typeof errorMessage === 'string' &&
		errorMessage.trim().length > 0
			? errorMessage
			: technicalError?.message || '';
	const resolvedTechnicalDetails =
		shouldUseResponseErrorData && Array.isArray(errorDetails)
			? errorDetails
			: shouldUseResponseErrorData &&
				  typeof errorDetails === 'string' &&
				  errorDetails.trim()
				? errorDetails
						.split(',')
						.map((value) => value.trim())
						.filter(Boolean)
				: technicalError?.details || [];

	const getStatusIcon = () => config.icon;
	const getTitle = () => config.title;
	const getSubtitle = () => config.subtitle;
	const getIconSize = () => config.iconSize;
	const pageTitle = getTitle();
	const pageSubtitle = getSubtitle();

	useEffect(() => {
		const message = pageSubtitle ? `${pageTitle}. ${pageSubtitle}` : pageTitle;
		setLiveMessage('');
		window.requestAnimationFrame(() => setLiveMessage(message));
	}, [statusCode, pageTitle, pageSubtitle]);

	const contentBox = (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				textAlign: 'center',
				gap: '8px',
			}}
		>
			{config.icon === 'spinner' ? (
				<CircularProgress
					size={56}
					role="status"
					aria-label={pageTitle}
					sx={{
						color: '#273C89',
					}}
				/>
			) : (
				<Box
					component="img"
					src={getStatusIcon()}
					alt=""
					aria-hidden
					sx={{
						width: getIconSize(),
						height: getIconSize(),
					}}
				/>
			)}
			<Typography
				id={PAGE_HEADING_ID}
				component="h1"
				sx={{
					fontSize: '16px',
					fontWeight: 700,
					color: theme.palette.text.primary,
					fontFamily: customDetails?.fontFamily,
				}}
			>
				{pageTitle}
			</Typography>
			{pageSubtitle && (
				<Typography
					sx={{
						fontSize: '14px',
						color: theme.palette.text.secondary,
						fontFamily: customDetails?.fontFamily,
					}}
				>
					{pageSubtitle}
				</Typography>
			)}
			<div role="status" aria-live="polite" style={visuallyHiddenSx}>
				{liveMessage}
			</div>
			{technicalError && (
				<>
					<Typography
						component="button"
						type="button"
						onClick={() => setShowDetails(true)}
						aria-label="View error details"
						sx={{
							fontSize: '13px',
							color: '#273C89',
							textDecoration: 'underline',
							background: 'none',
							border: 0,
							padding: '0.25rem',
							cursor: 'pointer',
							fontFamily: customDetails?.fontFamily,
							borderRadius: '4px',
							...keyboardFocusVisibleSx,
						}}
					>
						View details
					</Typography>
					{mobileScreen ? (
						<Drawer
							anchor="bottom"
							open={showDetails}
							onClose={() => setShowDetails(false)}
							hideBackdrop
							aria-labelledby={VOICE_ERROR_DETAILS_TITLE_ID}
							ModalProps={{
								disableScrollLock: true,
							}}
							PaperProps={{
								sx: {
									borderTopLeftRadius: '10px',
									borderTopRightRadius: '10px',
									backgroundColor: theme.palette.background.paper,
									boxShadow: 'none',
									borderTop: `1px solid ${theme.palette.divider}`,
									borderBottom: `1px solid ${theme.palette.divider}`,
									// Keep drawer above footer area.
									mb: `${mobileFooterHeight}px`,
								},
							}}
						>
							<Box
								sx={{
									width: '100%',
									backgroundColor: theme.palette.background.paper,
									textAlign: 'left',
								}}
							>
								<Box sx={{ p: '16px 16px 12px 16px' }}>
									<Typography
										id={VOICE_ERROR_DETAILS_TITLE_ID}
										sx={{
											fontSize: '18px',
											fontWeight: 700,
											color: theme.palette.text.primary,
											fontFamily: customDetails?.fontFamily,
											mb: '12px',
										}}
									>
										Error Details
									</Typography>
									<Box
										sx={{
											backgroundColor: '#f5f5f5',
											borderRadius: '8px',
											p: '12px',
										}}
									>
										<Typography
											sx={{
												fontSize: '20px',
												fontWeight: 700,
												letterSpacing: '0.03em',
												color: '#555',
												fontFamily: customDetails?.fontFamily,
											}}
										>
											{resolvedErrorCode}
										</Typography>
										<Typography
											sx={{
												fontSize: '14px',
												color: theme.palette.text.primary,
												mt: '6px',
												fontFamily: customDetails?.fontFamily,
											}}
										>
											{resolvedTechnicalMessage}
										</Typography>
										{resolvedTechnicalDetails.length > 0 && (
											<Box component="ul" sx={{ m: '8px 0 0 18px', p: 0 }}>
												{resolvedTechnicalDetails.map((detail) => (
													<Typography
														component="li"
														key={detail}
														sx={{
															fontSize: '14px',
															color: theme.palette.text.primary,
															fontFamily: customDetails?.fontFamily,
														}}
													>
														{detail}
													</Typography>
												))}
											</Box>
										)}
									</Box>
								</Box>
								<Divider />
								<Box sx={{ p: '14px 16px' }}>
									<Button
										fullWidth
										variant="outlined"
										disableElevation
										onClick={() => setShowDetails(false)}
										sx={{
											textTransform: 'none',
											fontWeight: 600,
											borderRadius: '8px',
											fontFamily: customDetails?.fontFamily,
											color: customDetails?.primaryColor || theme.palette.primary.main,
											border: `1px solid ${customDetails?.primaryColor || theme.palette.primary.main}`,
											backgroundColor: 'transparent !important',
											'&:hover': {
												border: `1px solid ${customDetails?.primaryColor || theme.palette.primary.main}`,
												backgroundColor: 'transparent !important',
											},
											'&:active': { backgroundColor: 'transparent !important' },
											'&:focus': { backgroundColor: 'transparent !important' },
											'&.Mui-focusVisible': { backgroundColor: 'transparent !important' },
										}}
									>
										Okay
									</Button>
								</Box>
							</Box>
						</Drawer>
					) : (
						<Dialog
							open={showDetails}
							onClose={() => setShowDetails(false)}
							maxWidth="sm"
							fullWidth
							aria-labelledby={VOICE_ERROR_DETAILS_TITLE_ID}
							PaperProps={{
								sx: {
									borderRadius: '12px',
									maxWidth: '480px',
								},
							}}
						>
							<Box
								sx={{
									width: '100%',
									backgroundColor: theme.palette.background.paper,
									textAlign: 'left',
								}}
							>
								<Box sx={{ p: '16px 16px 12px 16px' }}>
									<Typography
										id={VOICE_ERROR_DETAILS_TITLE_ID}
										sx={{
											fontSize: '18px',
											fontWeight: 700,
											color: theme.palette.text.primary,
											fontFamily: customDetails?.fontFamily,
											mb: '12px',
										}}
									>
										Error Details
									</Typography>
									<Box
										sx={{
											backgroundColor: '#f5f5f5',
											borderRadius: '8px',
											p: '12px',
										}}
									>
										<Typography
											sx={{
												fontSize: '20px',
												fontWeight: 700,
												letterSpacing: '0.03em',
												color: '#555',
												fontFamily: customDetails?.fontFamily,
											}}
										>
											{resolvedErrorCode}
										</Typography>
										<Typography
											sx={{
												fontSize: '14px',
												color: theme.palette.text.primary,
												mt: '6px',
												fontFamily: customDetails?.fontFamily,
											}}
										>
											{resolvedTechnicalMessage}
										</Typography>
										{resolvedTechnicalDetails.length > 0 && (
											<Box component="ul" sx={{ m: '8px 0 0 18px', p: 0 }}>
												{resolvedTechnicalDetails.map((detail) => (
													<Typography
														component="li"
														key={detail}
														sx={{
															fontSize: '14px',
															color: theme.palette.text.primary,
															fontFamily: customDetails?.fontFamily,
														}}
													>
														{detail}
													</Typography>
												))}
											</Box>
										)}
									</Box>
								</Box>
								<Divider />
								<Box sx={{ p: '14px 16px' }}>
									<Button
										fullWidth
										variant="outlined"
										disableElevation
										onClick={() => setShowDetails(false)}
										sx={{
											textTransform: 'none',
											fontWeight: 600,
											borderRadius: '8px',
											fontFamily: customDetails?.fontFamily,
											color: customDetails?.primaryColor || theme.palette.primary.main,
											border: `1px solid ${customDetails?.primaryColor || theme.palette.primary.main}`,
											backgroundColor: 'transparent !important',
											'&:hover': {
												border: `1px solid ${customDetails?.primaryColor || theme.palette.primary.main}`,
												backgroundColor: 'transparent !important',
											},
											'&:active': { backgroundColor: 'transparent !important' },
											'&:focus': { backgroundColor: 'transparent !important' },
											'&.Mui-focusVisible': { backgroundColor: 'transparent !important' },
										}}
									>
										Okay
									</Button>
								</Box>
							</Box>
						</Dialog>
					)}
				</>
			)}
		</Box>
	);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				minHeight: '100vh',
				paddingTop: mobileScreen ? '80px' : '96px',
				backgroundColor: theme.palette.primary.light,
			}}
		>
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					padding: mobileScreen ? '2rem 1.5rem' : '2rem',
				}}
			>
				{mobileScreen ? (
					contentBox
				) : (
					<Box
						sx={{
							padding: '3rem 4rem',
							maxWidth: '600px',
							minWidth: '500px',
							border: `1px solid ${theme.palette.divider}`,
							minHeight: '300px',
							borderRadius: '8px',
							backgroundColor: theme.palette.background.paper,
							boxShadow: 'none',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						{contentBox}
					</Box>
				)}
			</Box>
			<VoiceJourneyFooter customDetails={customDetails} />
		</Box>
	);
};

export default VoiceJourneyRedirectionCard;
